import { Autocomplete, CircularProgress, InputAdornment, TextField } from "@mui/material"
import { Person, Search } from "@mui/icons-material"
import React, { ChangeEvent, forwardRef, ReactNode, useImperativeHandle, useState } from "react"
import styled from "styled-components"
import { theme } from "../../../theme"
import { ButtonArea } from "../../atoms/button"
import { TextDisclaimer, TextLink } from "../../atoms/text"
import { DateFormatPreference, formatDate, formatPatientName } from "../../formatters"
import { ShortPatientProfile, ShortProfileInfo } from "../../molecules/profile"

export type PatientAutocompleteProps = {
  value: ShortProfileInfo[]
  onChange: (patients: ShortProfileInfo[]) => void
  onClickPatient: (patientId: string) => void
  onInputChange: (keyword: string) => void
  options?: ShortProfileInfo[]
  className?: string
  label?: string
  helperText?: string
  error?: boolean
  disabled?: boolean
  multiple?: boolean
  /* Autocomplete value only works if it's included in the option list,
  if we want to set it a value which doesn't belong to the list,
  we should pass it to this additionalOptions prop */
  additionalOptions?: ShortProfileInfo[]
  inputEndAdornment?: ReactNode
  onSave?: () => void
  loading?: boolean
  dateFormat: DateFormatPreference
  optionLink?: string
  optionLinkText?: string
  bottomContent?: {
    content: string
    buttonText: string
    onOpenPatientForm?: () => void
  }
}

export type PatientAutocompleteHandle = {
  reset: () => void
}

export const PatientAutocomplete = forwardRef((props: PatientAutocompleteProps, ref) => {
  useImperativeHandle(ref, () => ({
    reset: () => {
      handleChange([])
    },
  }))

  const {
    value = [],
    onChange,
    onInputChange,
    multiple = false,
    error = false,
    disabled = false,
    bottomContent,
    onClickPatient,
  } = props

  const allOptions = [...(props.options || []), ...(props.additionalOptions || [])]
  const [open, setOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const shouldShrinkLabel = isFocused || value.length > 0

  const handleFocus = () => {
    setOpen(true)
    setIsFocused(true)
  }

  const handleBlur = () => {
    setOpen(false)
    setIsFocused(false)
  }

  const handleChange = (newPatients: ShortProfileInfo[]) => {
    onChange(newPatients)
  }

  const handleAutocompleteChange = (e: any, newPatients: ShortProfileInfo[]) => {
    if (multiple || newPatients.length === 0) {
      handleChange(newPatients)
    } else {
      const newPatient = newPatients.length > 1 ? newPatients[1] : newPatients[0]
      handleChange([newPatient])
    }
    setOpen(false)
  }

  const handleOpenPatientForm = () => {
    handleChange([])

    if (bottomContent?.onOpenPatientForm) {
      bottomContent.onOpenPatientForm()
    }

    setOpen(false)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setOpen(true)
    onInputChange(e.target.value)
  }

  const handleClose = (e: any) => {
    if (e.target && e.target.className.includes("MuiAutocomplete-input")) return
    if (e.relatedTarget && e.relatedTarget.className.includes("MuiChip-root")) return

    setOpen(false)
  }

  const getEndAdornment = () => {
    if (disabled) return null

    if (props.loading) {
      return <CircularProgress color="primary" size={24} />
    }
    if (props.inputEndAdornment) {
      return <InputAdornment position="end">{props.inputEndAdornment}</InputAdornment>
    }

    return null
  }

  const renderBottomContent = (isNoOptionsContent = true) => {
    if (!bottomContent) return

    const { content, buttonText, onOpenPatientForm } = bottomContent
    const noOptionsContent = isNoOptionsContent ? (
      <TextDisclaimer>
        {content} {buttonText}
      </TextDisclaimer>
    ) : (
      ""
    )

    return (
      <NotFoundContent $isNoOptionsContent={isNoOptionsContent}>
        {onOpenPatientForm ? (
          <>
            <TextDisclaimer>{content}</TextDisclaimer>
            <CustomButtonArea
              onMouseDown={(e) => {
                e.preventDefault()
                handleOpenPatientForm()
              }}
            >
              {buttonText}
            </CustomButtonArea>
          </>
        ) : (
          noOptionsContent
        )}
      </NotFoundContent>
    )
  }

  return (
    <Autocomplete
      className={props.className}
      multiple
      filterSelectedOptions
      disableClearable
      open={open}
      disabled={disabled}
      onClose={handleClose}
      value={value}
      options={allOptions}
      filterOptions={(currentOptions) => currentOptions}
      getOptionLabel={(option) => formatPatientName(option.firstName, option.lastName, option.middleName)}
      onChange={handleAutocompleteChange}
      isOptionEqualToValue={(option, v) => option.id === v?.id}
      renderInput={(params) => (
        <InputContainer>
          <CustomTextField
            {...params}
            disabled={disabled}
            variant="outlined"
            label={props.label}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            error={error}
            helperText={props.helperText}
            InputLabelProps={{
              shrink: shouldShrinkLabel,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <IconWrapper>{!value.length ? <Search /> : <Person />}</IconWrapper>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: getEndAdornment(),
            }}
          />
        </InputContainer>
      )}
      groupBy={() => ""}
      renderGroup={(groupParams) => (
        <div key={groupParams.key}>
          {groupParams.children}
          {renderBottomContent(false)}
        </div>
      )}
      renderOption={(renderProps, patient) => (
        <li key={patient.id} {...renderProps}>
          <SimilarPatientContainer>
            <ShortPatientProfile
              row
              nameBold
              patient={{ ...patient, birthDate: formatDate(patient.birthDate, props.dateFormat) }}
            />
            <TextLink onClick={() => onClickPatient(patient.id!)}>{props.optionLinkText}</TextLink>
          </SimilarPatientContainer>
        </li>
      )}
      noOptionsText={renderBottomContent()}
      renderTags={(patients: ShortProfileInfo[]) =>
        patients.map((patient) => (
          <ShortPatientProfile
            row
            nameBold
            showIcon={false}
            key={patient.id}
            patient={{ ...patient, birthDate: formatDate(patient.birthDate, props.dateFormat) }}
          />
        ))
      }
    />
  )
})

const NotFoundContent = styled.div<{ $isNoOptionsContent?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.m};
  padding: 0 ${(p) => (p.$isNoOptionsContent ? 0 : theme.spacing.l)};
`

const InputContainer = styled.div`
  position: relative;

  .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] {
    padding-right: 9px;
  }
`

const SimilarPatientContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CustomTextField = styled(TextField)`
  .MuiInputLabel-root[data-shrink="false"] {
    margin-left: ${theme.spacing.xl};
  }
`

const CustomButtonArea = styled(ButtonArea)`
  color: ${theme.color.primary};
  align-items: center;
  font-weight: 700;
`

const IconWrapper = styled.div`
  margin-right: ${theme.spacing.s};
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    color: ${theme.color.grey2};
  }
`
